import { store } from '../../store/store';
import { api } from '../../store/services/api';
import { demoApi } from '../../store/services/demoApi';

/**
 * Viewport-lazy loader for Overview card details.
 *
 * The Overview can show 100+ cards; fetching every card's chart up-front floods
 * candle-fetcher and takes minutes. Instead each card reports when it scrolls
 * into view and we batch the newly-visible ids into one request (debounced).
 * Each id is requested at most once. Dispatched straight to the store so cards
 * don't need to thread a callback down the tree.
 */

const requestedIds = new Set<number>();
let pendingIds = new Set<number>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const FLUSH_DELAY_MS = 250;

export const requestSubscriptionDetails = (
  userId: number,
  subscriptionId: number,
) => {
  if (requestedIds.has(subscriptionId) || pendingIds.has(subscriptionId)) {
    return;
  }

  pendingIds.add(subscriptionId);

  if (flushTimer) {
    clearTimeout(flushTimer);
  }

  flushTimer = setTimeout(() => {
    const subscriptionIds = [...pendingIds];
    pendingIds = new Set();
    flushTimer = null;

    subscriptionIds.forEach((id) => requestedIds.add(id));

    if (!subscriptionIds.length) return;

    const state = store.getState();
    const isDemo = state.demo?.isDemo === true;

    if (isDemo) {
      const baseAssets = subscriptionIds
        .map((id) => state.subscription.subscriptions[id]?.baseAsset)
        .filter((b): b is string => typeof b === 'string' && b.length > 0);
      if (baseAssets.length) {
        store.dispatch(
          demoApi.endpoints.getDemoSubscriptionsDetailsBatch.initiate({
            baseAssets,
          }),
        );
      }
      return;
    }

    store.dispatch(
      api.endpoints.getSubscriptionsDetailsBatch.initiate({
        userId,
        subscriptionIds,
      }),
    );
  }, FLUSH_DELAY_MS);
};
